// Commission plan management.
// Phase 1 (shipped): tier rates + deductions + per-rep plan assignment.
// Phase 2 (shipped): custom tier ladders, cumulative-volume tiers.
// Phase 3 (this file): base-field selection, minimum quota gating.
// Phase 4 (next): chargebacks ledger (its own table + UI).
import React from 'react';
import { Icon, Pill, Button, Card, Banner, Section } from './ui.jsx';
import { api } from './api.js';
import { hasPermission } from './auth.jsx';

const toPct = (n) => (n == null ? '' : (Number(n) * 100).toFixed(2));
const fromPct = (s) => {
  if (s === '' || s == null) return null;
  const n = Number(s);
  if (!isFinite(n) || n < 0 || n > 100) return null;
  return n / 100;
};

export function ScreenCommissionPlans({ onNav, currentUser }) {
  const [plans, setPlans]       = React.useState([]);
  const [loading, setLoading]   = React.useState(true);
  const [creating, setCreating] = React.useState(false);
  const [newName, setNewName]   = React.useState('');
  const [newError, setNewError] = React.useState(null);
  const [busy, setBusy]         = React.useState(false);
  // After creating a plan, auto-open it in edit mode by passing its id down.
  const [autoEditId, setAutoEditId] = React.useState(null);
  // Gated on plans.edit / plans.delete permissions, not on legacy
  // role==='admin' (which would let an Approver edit plans — wrong).
  const canEditPlans   = hasPermission(currentUser, 'plans', 'edit');
  const canDeletePlans = hasPermission(currentUser, 'plans', 'delete');
  const isAdmin = canEditPlans; // legacy variable retained where edit is the gate

  React.useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const list = await api.commissionPlans.list();
        if (alive) setPlans(list);
      } catch (err) { console.error('plans load:', err); }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, []);

  const onPlanSaved = (updated) => {
    setPlans(ps => ps.map(p => p.id === updated.id ? updated : p));
  };
  const onPlanDeleted = (id) => {
    setPlans(ps => ps.filter(p => p.id !== id));
  };

  // Create a new plan. We seed it from the current default plan (if any)
  // so admins get a working tier ladder + deductions to edit from, rather
  // than a blank slate. The new plan is never marked is_default — there's
  // exactly one default per tenant, set at seed time.
  const createPlan = async () => {
    setNewError(null);
    const name = newName.trim();
    if (!name) { setNewError('Give the plan a name.'); return; }
    if (plans.some(p => p.name.toLowerCase() === name.toLowerCase())) {
      setNewError('A plan with that name already exists.'); return;
    }
    const seed = plans.find(p => p.isDefault) || plans[0];
    setBusy(true);
    try {
      const created = await api.commissionPlans.create({
        name,
        baseField:  seed?.baseField  || 'gross_profit',
        tierBasis:  seed?.tierBasis  || 'per_invoice_tenure',
        tiers:      seed?.tiers      || [],
        deductions: seed?.deductions || [],
        minQuota:   0,
        quotaMode:  'none',
        isDefault:  false,
      });
      setPlans(ps => [...ps, created]);
      setAutoEditId(created.id);
      setCreating(false); setNewName('');
    } catch (err) {
      setNewError('Could not create plan: ' + (err.message || err));
    } finally { setBusy(false); }
  };

  if (loading) return <div className="p-10 text-center text-neutral-500 text-[13px]">Loading commission plans…</div>;

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-4">
        <div>
          <h1 className="text-[24px] font-bold text-neutral-900 tracking-[-0.01em]">Commission plans</h1>
          <div className="text-[13px] text-neutral-500 mt-1">
            The default plan applies to every rep unless they're individually assigned a different one
            (set on each rep under <b>Users → Edit</b>).
          </div>
        </div>
        {isAdmin && !creating && (
          <Button variant="primary" size="md" icon="plus" onClick={() => { setCreating(true); setNewError(null); setNewName(''); }}>
            New plan
          </Button>
        )}
      </div>

      {/* Inline "create plan" form — kept minimal: just a name. The new plan
          is seeded from the default so the admin has a working ladder to
          edit from rather than a blank slate. */}
      {isAdmin && creating && (
        <Card className="border-primary-300 bg-primary-100/10">
          <div className="text-[13px] font-semibold text-neutral-900 mb-1">Create a new plan</div>
          <div className="text-[11.5px] text-neutral-600 mb-3 leading-relaxed">
            We'll start from the default plan's tiers and deductions so you have something to edit.
            You can change everything after.
          </div>
          <div className="flex items-center gap-2">
            <input
              autoFocus
              value={newName}
              onChange={e => setNewName(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && !busy) createPlan(); if (e.key === 'Escape') setCreating(false); }}
              placeholder="e.g. Senior reps, Cumulative volume, New-hire onboarding"
              disabled={busy}
              className="flex-1 h-10 px-3 text-[14px] rounded-md border border-primary-300 bg-white focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-700 disabled:opacity-50"/>
            <Button variant="primary" size="md" icon="check" onClick={createPlan} disabled={busy}>
              {busy ? 'Creating…' : 'Create'}
            </Button>
            <Button variant="ghost" size="md" onClick={() => setCreating(false)} disabled={busy}>Cancel</Button>
          </div>
          {newError && <div className="mt-3 text-[12px] text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">{newError}</div>}
        </Card>
      )}

      {!isAdmin && (
        <Banner tone="info" icon="lock" title="Read-only">
          Only admins can edit commission plans.
        </Banner>
      )}

      {plans.length === 0 ? (
        <Banner tone="warn" icon="warn" title="No plans yet">
          Run the migration to seed your default plan, or contact support.
        </Banner>
      ) : (
        plans.map(p => (
          <PlanCard key={p.id} plan={p} isAdmin={isAdmin}
            onSaved={onPlanSaved}
            onDeleted={onPlanDeleted}
            autoEdit={autoEditId === p.id}
            clearAutoEdit={() => setAutoEditId(null)}/>
        ))
      )}

      <div className="text-[12px] text-neutral-500 pt-4 border-t border-neutral-200">
        <b>Coming next:</b> chargebacks ledger — track AR write-offs and clawback paid commissions on a follow-up run.
      </div>
    </div>
  );
}

// ----- Tier shape helpers -----
// Tier is editable as { label, rate (% string), min, max } where min/max
// are in months (per-invoice mode) or dollars (cumulative mode).
function tiersToEditable(tiers) {
  return (tiers || []).map(t => {
    const c = t.criteria || {};
    return {
      label: t.label || '',
      rate:  toPct(t.rate),
      min:   c.min == null ? '' : String(c.min),
      max:   c.max == null ? '' : String(c.max),
    };
  });
}
function tiersFromEditable(editable, basis) {
  const criteriaType = basis === 'cumulative_period' ? 'cumulative_amount' : 'client_tenure_months';
  return editable.map(t => ({
    label: t.label || '',
    rate:  fromPct(t.rate),
    criteria: {
      type: criteriaType,
      min:  t.min === '' || t.min == null ? null : Number(t.min),
      max:  t.max === '' || t.max == null ? null : Number(t.max),
    },
  }));
}

// ----- One plan card with inline edit -----
function PlanCard({ plan, isAdmin, onSaved, onDeleted, autoEdit, clearAutoEdit }) {
  const [editing, setEditing] = React.useState(false);
  const [saving, setSaving]   = React.useState(false);
  const [error, setError]     = React.useState(false ? '' : null);
  const [confirmDelete, setConfirmDelete] = React.useState(false);

  // When the parent created this plan via the "+ New plan" flow, jump
  // straight into edit mode so the admin can configure tiers/quota.
  React.useEffect(() => {
    if (autoEdit) {
      setEditing(true);
      clearAutoEdit?.();
    }
  }, [autoEdit, clearAutoEdit]);

  const [name, setName]             = React.useState(plan.name);
  const [tierBasis, setTierBasis]   = React.useState(plan.tierBasis || 'per_invoice_tenure');
  const [deductions, setDeductions] = React.useState((plan.deductions || []).map(d => ({ ...d, rate: toPct(d.rate) })));
  const [tiers, setTiers]           = React.useState(tiersToEditable(plan.tiers));
  const [baseField, setBaseField]   = React.useState(plan.baseField || 'gross_profit');
  const [minQuota, setMinQuota]     = React.useState(plan.minQuota == null ? '' : String(plan.minQuota));
  const [quotaMode, setQuotaMode]   = React.useState(plan.quotaMode || 'none');
  const [paymentBasis, setPaymentBasis] = React.useState(plan.paymentBasis || 'accrual');

  React.useEffect(() => {
    setName(plan.name);
    setTierBasis(plan.tierBasis || 'per_invoice_tenure');
    setDeductions((plan.deductions || []).map(d => ({ ...d, rate: toPct(d.rate) })));
    setTiers(tiersToEditable(plan.tiers));
    setBaseField(plan.baseField || 'gross_profit');
    setMinQuota(plan.minQuota == null ? '' : String(plan.minQuota));
    setQuotaMode(plan.quotaMode || 'none');
    setPaymentBasis(plan.paymentBasis || 'accrual');
    setEditing(false); setError(null);
  }, [plan.id, plan.updatedAt]);

  const reset = () => {
    setName(plan.name);
    setTierBasis(plan.tierBasis || 'per_invoice_tenure');
    setDeductions((plan.deductions || []).map(d => ({ ...d, rate: toPct(d.rate) })));
    setTiers(tiersToEditable(plan.tiers));
    setBaseField(plan.baseField || 'gross_profit');
    setMinQuota(plan.minQuota == null ? '' : String(plan.minQuota));
    setQuotaMode(plan.quotaMode || 'none');
    setPaymentBasis(plan.paymentBasis || 'accrual');
    setError(null);
  };
  const cancel = () => { reset(); setEditing(false); setConfirmDelete(false); };

  // Delete a plan. The default plan is protected from deletion both here
  // and in the migration's RLS policies. Reps assigned to the deleted plan
  // automatically fall back to the tenant default on next calculation.
  const deletePlan = async () => {
    setError(null); setSaving(true);
    try {
      await api.commissionPlans.delete(plan.id);
      onDeleted?.(plan.id);
    } catch (err) {
      setSaving(false);
      setError('Could not delete plan: ' + (err.message || err));
    }
  };

  const addTier = () => {
    const last = tiers[tiers.length - 1];
    setTiers([
      ...tiers,
      {
        label: `Tier ${tiers.length + 1}`,
        rate:  '',
        min:   last && last.max !== '' ? last.max : '',
        max:   '',
      },
    ]);
  };
  const removeTier = (idx) => setTiers(tiers.filter((_, i) => i !== idx));
  const setTier = (idx, patch) => setTiers(tiers.map((t, i) => i === idx ? { ...t, ...patch } : t));

  const save = async () => {
    setError(null);
    const dParsed = deductions.map(d => ({
      label: (d.label || '').trim(),
      field: d.field || 'gross_wages',
      rate:  fromPct(d.rate),
    }));
    if (dParsed.some(d => d.rate === null)) {
      setError('Deduction rates must be numbers between 0 and 100.'); return;
    }
    if (dParsed.some(d => !d.label)) {
      setError('Every deduction needs a label (e.g. ACA, SICK, Workers Comp).'); return;
    }
    if (tiers.length === 0) {
      setError('At least one tier is required.'); return;
    }
    const tParsed = tiersFromEditable(tiers, tierBasis);
    if (tParsed.some(t => t.rate === null)) {
      setError('Tier rates must be numbers between 0 and 100.'); return;
    }
    if (tParsed.some(t => !t.label?.trim())) {
      setError('Every tier needs a label (e.g. Y1, Tier A, 0–50K).'); return;
    }
    // Sanity: thresholds must be ascending in cumulative mode.
    if (tierBasis === 'cumulative_period') {
      let prevMax = 0;
      for (const t of tParsed) {
        const min = t.criteria.min == null ? 0 : t.criteria.min;
        const max = t.criteria.max;
        if (min < prevMax) { setError('Cumulative tier brackets must not overlap. Each tier should start where the previous one ended.'); return; }
        if (max != null && max <= min) { setError(`Tier "${t.label}" — Max must be greater than Min.`); return; }
        prevMax = max == null ? Infinity : max;
      }
    }

    // Quota validation. Empty string is fine — treated as 0/none.
    const mqNum = minQuota === '' || minQuota == null ? 0 : Number(minQuota);
    if (!isFinite(mqNum) || mqNum < 0) {
      setError('Minimum quota must be 0 or a positive dollar amount.'); return;
    }
    if (quotaMode !== 'none' && mqNum === 0) {
      setError('Choose a quota mode of "None" if no quota applies, or enter a non-zero minimum.'); return;
    }

    setSaving(true);
    try {
      const updated = await api.commissionPlans.update(plan.id, {
        name: name.trim() || plan.name,
        tierBasis,
        deductions: dParsed,
        tiers: tParsed,
        baseField,
        minQuota: mqNum,
        quotaMode,
        paymentBasis,
      });
      onSaved(updated);
      setEditing(false);
    } catch (err) {
      setError('Save failed: ' + err.message);
    } finally { setSaving(false); }
  };

  // Human-readable label for base field (used in the read-only sub line).
  const BASE_LABELS = {
    gross_profit: 'gross profit',
    total_bill:   'total bill',
    gross_wages:  'gross wages',
    net:          'net (after deductions)',
  };
  const baseLabel = BASE_LABELS[plan.baseField] || (plan.baseField || 'gross_profit').replace('_', ' ');
  const quotaLabel =
    plan.quotaMode === 'all_or_nothing' ? `Quota gate: $${(plan.minQuota || 0).toLocaleString()} all-or-nothing`
  : plan.quotaMode === 'above_only'     ? `Quota gate: pay only above $${(plan.minQuota || 0).toLocaleString()}`
                                        : null;

  return (
    <Section
      title={
        <span className="inline-flex items-center gap-2">
          <span>{plan.name}</span>
          {plan.isDefault && <Pill tone="finalized">Default</Pill>}
        </span>
      }
      sub={[
        `Base: ${baseLabel}`,
        plan.tierBasis === 'cumulative_period' ? 'Cumulative volume tiers' : 'Per-invoice tenure tiers',
        `${(plan.tiers || []).length} bracket${(plan.tiers || []).length === 1 ? '' : 's'}`,
        quotaLabel,
      ].filter(Boolean).join(' · ')}
      right={isAdmin && !editing ? (
        <Button variant="secondary" size="sm" onClick={() => setEditing(true)}>
          <span className="inline-flex items-center gap-1.5"><Icon name="override" size={13}/> Edit plan</span>
        </Button>
      ) : null}
    >
      {/* Base field — what column the rate multiplies against */}
      <Card className="mb-3">
        <div className="flex items-center justify-between gap-4">
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-[0.06em] text-neutral-500 mb-1">Base field</div>
            <div className="text-[12px] text-neutral-600 leading-relaxed max-w-[480px]">
              The column each rate is applied to.{' '}
              {baseField === 'gross_profit' && <span><b>Gross profit</b> is what most agencies use — bill minus wages.</span>}
              {baseField === 'total_bill'   && <span><b>Total bill</b> rewards top-line revenue regardless of margin.</span>}
              {baseField === 'gross_wages'  && <span><b>Gross wages</b> is unusual — typically only used for hourly-volume plans.</span>}
              {baseField === 'net'          && <span><b>Net</b> = gross profit minus ACA &amp; sick (already calculated upstream). Plan deductions below are skipped to avoid double-subtracting.</span>}
            </div>
          </div>
          {editing ? (
            <select value={baseField} onChange={e => setBaseField(e.target.value)}
              className="h-9 px-3 text-[13px] rounded-md border border-primary-300 bg-primary-100/20 focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-700">
              <option value="gross_profit">Gross profit</option>
              <option value="total_bill">Total bill</option>
              <option value="gross_wages">Gross wages</option>
              <option value="net">Net (after deductions)</option>
            </select>
          ) : (
            <Pill tone="brand">{BASE_LABELS[baseField] || baseField}</Pill>
          )}
        </div>
      </Card>

      {/* Deductions — fully editable. Each deduction subtracts
          (rate × invoice[field]) from the base before the tier rate is
          applied. Common examples: ACA on gross_wages, SICK on gross_wages,
          a customer-specific fee on gross_profit, etc.
          Note: when baseField is 'net' the engine skips all deductions
          (net is already gross_profit minus ACA + SICK from the parser). */}
      <Card className="mb-3">
        <div className="flex items-center justify-between mb-3">
          <div className="text-[11px] font-semibold uppercase tracking-[0.06em] text-neutral-500">Deductions (applied before tier rate)</div>
          {editing && (
            <button
              onClick={() => setDeductions([...deductions, { label: '', field: 'gross_wages', rate: '' }])}
              className="text-[12px] text-primary-700 hover:text-primary-900 font-medium inline-flex items-center gap-1">
              <Icon name="plus" size={12}/> Add deduction
            </button>
          )}
        </div>
        {deductions.length === 0 && (
          <div className="text-[12px] text-neutral-500">
            {editing
              ? 'No deductions yet. Add one (e.g. ACA 1.80% on gross wages, SICK 1.20% on gross wages).'
              : 'No deductions configured.'}
          </div>
        )}
        {deductions.length > 0 && editing && (
          <div className="space-y-2">
            <div className="grid grid-cols-[1fr_180px_120px_36px] gap-2 px-1 text-[10.5px] font-semibold uppercase tracking-[0.06em] text-neutral-500">
              <div>Label</div>
              <div>Applied to</div>
              <div className="text-right">Rate</div>
              <div/>
            </div>
            {deductions.map((d, i) => (
              <div key={i} className="grid grid-cols-[1fr_180px_120px_36px] gap-2 items-center">
                <input
                  type="text" value={d.label || ''}
                  onChange={e => setDeductions(deductions.map((x, j) => j === i ? { ...x, label: e.target.value } : x))}
                  placeholder="e.g. ACA"
                  className="h-9 px-3 text-[13px] rounded-md border border-primary-300 bg-primary-100/20 focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-700"/>
                <select
                  value={d.field || 'gross_wages'}
                  onChange={e => setDeductions(deductions.map((x, j) => j === i ? { ...x, field: e.target.value } : x))}
                  className="h-9 px-2.5 text-[13px] rounded-md border border-primary-300 bg-primary-100/20 focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-700">
                  <option value="gross_wages">gross wages</option>
                  <option value="gross_profit">gross profit</option>
                  <option value="total_bill">total bill</option>
                </select>
                <div className="relative">
                  <input
                    type="number" step="0.01" min="0" max="100"
                    value={d.rate}
                    onChange={e => setDeductions(deductions.map((x, j) => j === i ? { ...x, rate: e.target.value } : x))}
                    className="w-full h-9 pl-3 pr-7 text-[13px] font-mono text-right rounded-md border border-primary-300 bg-primary-100/20 focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-700"/>
                  <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[12px] text-neutral-400 font-mono">%</span>
                </div>
                <button
                  onClick={() => setDeductions(deductions.filter((_, j) => j !== i))}
                  title="Remove deduction"
                  className="h-9 w-9 rounded-md border border-neutral-200 text-neutral-500 hover:bg-red-50 hover:border-red-200 hover:text-red-700 flex items-center justify-center">
                  <Icon name="close" size={13}/>
                </button>
              </div>
            ))}
          </div>
        )}
        {deductions.length > 0 && !editing && (
          <div className="grid grid-cols-2 gap-4">
            {deductions.map((d, i) => (
              <div key={i} className="flex items-center justify-between gap-3">
                <div className="text-[13px] text-neutral-700">
                  {d.label || '(unnamed)'}{' '}
                  <span className="text-neutral-400 text-[11px]">on {String(d.field || 'gross_wages').replace('_', ' ')}</span>
                </div>
                <div className="text-[14px] font-mono font-semibold text-neutral-900 tabular-nums">{Number(d.rate || 0).toFixed(2)}%</div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Tier basis */}
      <Card className="mb-3">
        <div className="flex items-center justify-between gap-4">
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-[0.06em] text-neutral-500 mb-1">Tier basis</div>
            <div className="text-[12px] text-neutral-600 leading-relaxed max-w-[480px]">
              {tierBasis === 'cumulative_period'
                ? <span><b>Cumulative.</b> Each tier covers a $-bracket of the rep's running base for the period. Invoices walk the brackets in date order.</span>
                : <span><b>Per invoice.</b> Each invoice independently picks a tier based on the customer's tenure (months since first invoice).</span>}
            </div>
          </div>
          {editing ? (
            <select value={tierBasis} onChange={e => setTierBasis(e.target.value)}
              className="h-9 px-3 text-[13px] rounded-md border border-primary-300 bg-primary-100/20 focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-700">
              <option value="per_invoice_tenure">Per invoice (tenure)</option>
              <option value="cumulative_period">Cumulative (volume)</option>
            </select>
          ) : (
            <Pill tone="brand">{tierBasis === 'cumulative_period' ? 'Cumulative' : 'Per invoice'}</Pill>
          )}
        </div>
      </Card>

      {/* Tier ladder — fully editable */}
      <Card>
        <div className="flex items-center justify-between mb-3">
          <div className="text-[11px] font-semibold uppercase tracking-[0.06em] text-neutral-500">Tier ladder</div>
          {editing && (
            <Button variant="secondary" size="sm" icon="plus" onClick={addTier}>Add tier</Button>
          )}
        </div>

        {/* Column headers */}
        <div className="grid grid-cols-[1.4fr_1fr_1fr_0.9fr_36px] gap-2 px-1 pb-2 text-[10.5px] font-semibold uppercase tracking-[0.06em] text-neutral-500">
          <div>Label</div>
          <div>{tierBasis === 'cumulative_period' ? 'From ($)' : 'From (months)'}</div>
          <div>{tierBasis === 'cumulative_period' ? 'To ($)' : 'To (months)'}</div>
          <div className="text-right">Rate</div>
          <div/>
        </div>

        <div className="space-y-2">
          {tiers.length === 0 && (
            <div className="text-[12px] text-neutral-500 text-center py-3 border border-dashed border-neutral-200 rounded">
              No tiers — click <b>Add tier</b> to create the first one.
            </div>
          )}
          {tiers.map((t, i) => (
            <div key={i} className="grid grid-cols-[1.4fr_1fr_1fr_0.9fr_36px] gap-2 items-center">
              {/* Label */}
              {editing ? (
                <input value={t.label} onChange={e => setTier(i, { label: e.target.value })}
                  placeholder={tierBasis === 'cumulative_period' ? 'Tier A' : 'Y1'}
                  className="h-9 px-2.5 text-[13px] rounded-md border border-neutral-300 bg-white focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-700"/>
              ) : (
                <div className="text-[13.5px] font-semibold text-neutral-900 px-2.5">{t.label}</div>
              )}
              {/* Min */}
              {editing ? (
                <input type="number" min="0" value={t.min} onChange={e => setTier(i, { min: e.target.value })}
                  placeholder="0"
                  className="h-9 px-2.5 text-[13px] font-mono rounded-md border border-neutral-300 bg-white focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-700"/>
              ) : (
                <div className="text-[12.5px] font-mono text-neutral-700 px-2.5">
                  {t.min === '' || t.min == null ? '0' : t.min}
                </div>
              )}
              {/* Max */}
              {editing ? (
                <input type="number" min="0" value={t.max} onChange={e => setTier(i, { max: e.target.value })}
                  placeholder="∞"
                  className="h-9 px-2.5 text-[13px] font-mono rounded-md border border-neutral-300 bg-white focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-700"/>
              ) : (
                <div className="text-[12.5px] font-mono text-neutral-700 px-2.5">
                  {t.max === '' || t.max == null ? '∞' : t.max}
                </div>
              )}
              {/* Rate */}
              {editing ? (
                <div className="relative">
                  <input type="number" step="0.01" min="0" max="100" value={t.rate}
                    onChange={e => setTier(i, { rate: e.target.value })}
                    className="w-full h-9 pl-2.5 pr-7 text-[13px] font-mono text-right rounded-md border border-primary-300 bg-primary-100/20 focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-700"/>
                  <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[12px] text-neutral-400 font-mono">%</span>
                </div>
              ) : (
                <div className="text-[15px] font-mono font-bold text-primary-700 tabular-nums text-right pr-2.5">
                  {Number(t.rate || 0).toFixed(2)}%
                </div>
              )}
              {/* Delete */}
              {editing ? (
                <button onClick={() => removeTier(i)}
                  className="w-9 h-9 rounded-md border border-neutral-200 text-neutral-400 hover:text-red-700 hover:border-red-300 flex items-center justify-center"
                  title="Remove tier">
                  <Icon name="x" size={14}/>
                </button>
              ) : <div/>}
            </div>
          ))}
        </div>

        {tierBasis === 'cumulative_period' && tiers.length > 0 && (
          <div className="mt-4 text-[11px] text-neutral-500 leading-relaxed border-t border-neutral-100 pt-3">
            Brackets are walked in order. The rep's first dollar of base goes through Tier 1's rate,
            and only the portion above Tier 1's max gets Tier 2's rate. Leave the last tier's "To" empty
            for "no cap".
          </div>
        )}
      </Card>

      {/* Minimum quota — gates the rep's payout for the period */}
      <Card className="mt-3">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <div className="text-[11px] font-semibold uppercase tracking-[0.06em] text-neutral-500 mb-1">Minimum quota</div>
            <div className="text-[12px] text-neutral-600 leading-relaxed max-w-[520px]">
              {quotaMode === 'none'           && <span>No quota — every rep earns commission on every invoice.</span>}
              {quotaMode === 'all_or_nothing' && <span><b>All-or-nothing.</b> If a rep's total base for the period is below the minimum, their entire commission for the period is zero.</span>}
              {quotaMode === 'above_only'     && <span><b>Above only.</b> A rep earns commission only on the portion of base above the minimum. If they don't hit the minimum, they earn zero.</span>}
            </div>
            {(quotaMode !== 'none') && !editing && (
              <div className="mt-2 text-[12px] text-neutral-500">
                Threshold: <span className="font-mono font-semibold text-neutral-900">${Number(plan.minQuota || 0).toLocaleString()}</span>{' '}
                of <span className="text-neutral-700">{baseLabel}</span> per rep per run.
              </div>
            )}
          </div>
          {editing ? (
            <div className="flex items-end gap-2 shrink-0">
              <div>
                <div className="text-[10.5px] font-semibold uppercase tracking-[0.06em] text-neutral-500 mb-1">Mode</div>
                <select value={quotaMode} onChange={e => setQuotaMode(e.target.value)}
                  className="h-9 px-3 text-[13px] rounded-md border border-primary-300 bg-primary-100/20 focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-700">
                  <option value="none">None</option>
                  <option value="all_or_nothing">All-or-nothing</option>
                  <option value="above_only">Above only</option>
                </select>
              </div>
              <div>
                <div className="text-[10.5px] font-semibold uppercase tracking-[0.06em] text-neutral-500 mb-1">Threshold ($)</div>
                <div className="relative w-36">
                  <span className="absolute left-2 top-1/2 -translate-y-1/2 text-[12px] text-neutral-400 font-mono">$</span>
                  <input type="number" min="0" step="100"
                    value={minQuota}
                    onChange={e => setMinQuota(e.target.value)}
                    disabled={quotaMode === 'none'}
                    placeholder="0"
                    className="w-full h-9 pl-6 pr-2.5 text-[13px] font-mono text-right rounded-md border border-primary-300 bg-primary-100/20 focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-700 disabled:opacity-40 disabled:cursor-not-allowed"/>
                </div>
              </div>
            </div>
          ) : (
            <Pill tone={quotaMode === 'none' ? 'neutral' : 'warn'}>
              {quotaMode === 'none' ? 'No quota' : quotaMode === 'all_or_nothing' ? 'All-or-nothing' : 'Above only'}
            </Pill>
          )}
        </div>
      </Card>

      {/* Payment basis — pay on every invoice vs. hold until AR is collected.
          Note: we keep the underlying values 'accrual'/'cash' for backward
          compatibility, but labels everywhere now describe the BEHAVIOR
          rather than the accounting term — staffing industry usage of
          'cash' often means the OPPOSITE of accounting usage, and we
          had a real-world bug from that. */}
      <Card className="mt-3">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <div className="text-[11px] font-semibold uppercase tracking-[0.06em] text-neutral-500 mb-1">When do reps earn commission?</div>
            <div className="text-[12px] text-neutral-600 leading-relaxed max-w-[520px]">
              {paymentBasis === 'accrual'
                ? <span><b>On every invoice.</b> Commission is earned the moment an invoice is created, regardless of whether the customer has paid. Reps get paid on bill — finance handles collection separately.</span>
                : <span><b>Only when the customer pays.</b> Commission is held at $0 on unpaid invoices and released as a carryover line item in the run AFTER the AR upload shows it collected.</span>}
            </div>
            {paymentBasis === 'cash' && (
              <div className="mt-2 text-[11.5px] text-neutral-500 leading-relaxed">
                Tip: re-uploading the AR file next month will mark previously-unpaid invoices as paid. Recalculate the affected run to release held commission.
              </div>
            )}
          </div>
          {editing ? (
            <div className="shrink-0">
              <div className="text-[10.5px] font-semibold uppercase tracking-[0.06em] text-neutral-500 mb-1">Setting</div>
              <select value={paymentBasis} onChange={e => setPaymentBasis(e.target.value)}
                className="h-9 px-3 text-[13px] rounded-md border border-primary-300 bg-primary-100/20 focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-700">
                <option value="accrual">Pay on every invoice</option>
                <option value="cash">Hold until customer pays</option>
              </select>
            </div>
          ) : (
            <Pill tone={paymentBasis === 'cash' ? 'warn' : 'neutral'}>
              {paymentBasis === 'cash' ? 'Hold until paid' : 'Pay on every invoice'}
            </Pill>
          )}
        </div>
      </Card>

      {error && <div className="mt-3 text-[12px] text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">{error}</div>}

      {editing && (
        <Card padding="p-4" className="mt-3 border-primary-300 bg-primary-100/10">
          <div className="flex items-center gap-2">
            {/* Delete is left-aligned, only on non-default plans, behind a confirm step */}
            {!plan.isDefault && (
              confirmDelete ? (
                <div className="flex items-center gap-2 mr-auto">
                  <span className="text-[12px] text-red-800 font-medium">Delete this plan? Reps assigned to it will fall back to the default.</span>
                  <Button variant="destructive" size="sm" onClick={deletePlan} disabled={saving}>
                    {saving ? 'Deleting…' : 'Yes, delete'}
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => setConfirmDelete(false)} disabled={saving}>No</Button>
                </div>
              ) : (
                <Button variant="ghost" size="sm" onClick={() => setConfirmDelete(true)} disabled={saving} className="mr-auto text-red-700 hover:text-red-900 hover:bg-red-50">
                  Delete plan
                </Button>
              )
            )}
            <Button variant="ghost" size="sm" onClick={cancel} disabled={saving}>Cancel</Button>
            <Button variant="primary" size="sm" icon="check" onClick={save} disabled={saving}>
              {saving ? 'Saving…' : 'Save changes'}
            </Button>
          </div>
        </Card>
      )}
    </Section>
  );
}
